import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, CalendarDays, CheckCircle2, Loader2 } from "lucide-react";
import { Chapter, Reveal } from "./Chapter";
import { CONTACT } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_BOOKING = {
  arrival: "",
  departure: "",
  type: "Mobil-home",
  guests: 2,
  name: "",
  email: "",
  phone: "",
  message: "",
};
const EMPTY_CONTACT = { name: "", email: "", subject: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-sand px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-pine/40 focus:border-pine transition-shadow";
const labelCls = "block text-[11px] font-bold tracking-[0.16em] uppercase text-ink/50 mb-1.5";

export default function Reservation() {
  const [tab, setTab] = useState("booking");
  const [booking, setBooking] = useState(EMPTY_BOOKING);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const submitBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        name: booking.name,
        email: booking.email,
        phone: booking.phone,
        accommodation_type: booking.type,
        arrival: booking.arrival,
        departure: booking.departure,
        guests: Number(booking.guests),
        message: booking.message,
      });
      setDone(data);
      toast.success("Demande de réservation envoyée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message envoyé ! Nous vous répondrons vite.");
      setContact(EMPTY_CONTACT);
    } catch {
      toast.error("Une erreur est survenue, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reservation" className="relative py-28 lg:py-36 px-6 sm:px-10 lg:px-16 bg-lakedeep text-sand grain" data-testid="section-reservation">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Chapter num="9" label="Réservation & contact" dark />
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              Le lac vous<br />
              <span className="italic font-medium text-gold">appelle déjà.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-sand/70 leading-relaxed max-w-md">
              Envoyez-nous votre demande : nous vous répondons personnellement pour
              confirmer disponibilités et tarifs. Bien préparer, c'est déjà voyager.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            {[
              { icon: MapPin, label: "Adresse", value: CONTACT.address, href: null, testid: "contact-address" },
              { icon: Phone, label: "Téléphone", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone" },
              { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
              { icon: CalendarDays, label: "Ouverture", value: `${CONTACT.season} · ${CONTACT.gps}`, href: null, testid: "contact-season" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={0.15 + i * 0.07}>
                <div className="flex items-start gap-4" data-testid={c.testid}>
                  <span className="grid place-items-center w-11 h-11 shrink-0 rounded-full bg-sand/10 border border-sand/15 text-gold">
                    <c.icon size={18} />
                  </span>
                  <div>
                    <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-sand/45">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} className="font-medium text-sand hover:text-gold transition-colors">
                        {c.value}
                      </a>
                    ) : (
                      <div className="font-medium text-sand">{c.value}</div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal className="lg:col-span-7" delay={0.15}>
          <div className="rounded-[2rem] bg-sand text-ink p-6 sm:p-10 shadow-2xl shadow-black/40">
            <div className="flex gap-2 p-1.5 rounded-full bg-cream w-max" data-testid="reservation-tabs">
              {[
                ["booking", "Demande de réservation"],
                ["contact", "Une question ?"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`tab-${key}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                    tab === key ? "bg-pine text-sand shadow" : "text-ink/60 hover:text-ink"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {tab === "booking" && !done && (
                <motion.form
                  key="booking"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.35 }}
                  onSubmit={submitBooking}
                  className="mt-8 grid sm:grid-cols-2 gap-5"
                  data-testid="booking-form"
                >
                  <div>
                    <label className={labelCls}>Arrivée</label>
                    <input type="date" required min={today} value={booking.arrival}
                      onChange={(e) => setBooking({ ...booking, arrival: e.target.value })}
                      className={inputCls} data-testid="booking-arrival-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Départ</label>
                    <input type="date" required min={booking.arrival || today} value={booking.departure}
                      onChange={(e) => setBooking({ ...booking, departure: e.target.value })}
                      className={inputCls} data-testid="booking-departure-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Hébergement</label>
                    <select value={booking.type}
                      onChange={(e) => setBooking({ ...booking, type: e.target.value })}
                      className={inputCls} data-testid="booking-type-select">
                      <option>Mobil-home</option>
                      <option>Emplacement tente / caravane</option>
                      <option>Hébergement insolite</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Voyageurs</label>
                    <input type="number" min="1" max="20" required value={booking.guests}
                      onChange={(e) => setBooking({ ...booking, guests: e.target.value })}
                      className={inputCls} data-testid="booking-guests-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Nom & prénom</label>
                    <input type="text" required placeholder="Marie Dupont" value={booking.name}
                      onChange={(e) => setBooking({ ...booking, name: e.target.value })}
                      className={inputCls} data-testid="booking-name-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Téléphone</label>
                    <input type="tel" required placeholder="06 12 34 56 78" value={booking.phone}
                      onChange={(e) => setBooking({ ...booking, phone: e.target.value })}
                      className={inputCls} data-testid="booking-phone-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Email</label>
                    <input type="email" required placeholder="marie@exemple.fr" value={booking.email}
                      onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                      className={inputCls} data-testid="booking-email-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Message (optionnel)</label>
                    <textarea rows="3" placeholder="Emplacement ombragé si possible, un lit bébé…" value={booking.message}
                      onChange={(e) => setBooking({ ...booking, message: e.target.value })}
                      className={`${inputCls} resize-none`} data-testid="booking-message-input" />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="booking-submit-button"
                    className="sm:col-span-2 rounded-full bg-terra text-sand font-semibold px-8 py-4 shadow-lg shadow-terra/25 transition-all duration-300 hover:bg-pine disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 size={17} className="animate-spin" />}
                    Envoyer ma demande
                  </button>
                </motion.form>
              )}

              {tab === "booking" && done && (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45 }}
                  className="mt-8 text-center py-8"
                  data-testid="booking-success-panel"
                >
                  <CheckCircle2 size={52} className="mx-auto text-pine" />
                  <h3 className="mt-5 font-display font-bold text-3xl text-ink">Demande bien reçue !</h3>
                  <p className="mt-3 text-ink/60 max-w-md mx-auto">
                    Merci {done.name.split(" ")[0]} ! Votre demande pour {done.nights} nuit{done.nights > 1 ? "s" : ""} en{" "}
                    {done.accommodation_type} ({done.guests} voyageur{done.guests > 1 ? "s" : ""}) est entre de bonnes mains.
                    Nous vous répondons très vite au {done.phone}.
                  </p>
                  <div className="mt-6 inline-block rounded-full bg-cream border border-pine/25 px-6 py-3 font-mono text-sm font-bold text-pine tracking-widest">
                    Réf. {done.reference}
                  </div>
                  <p className="mt-5 font-hand text-2xl text-terra -rotate-2">à très vite au bord du lac !</p>
                  <button
                    onClick={() => { setDone(null); setBooking(EMPTY_BOOKING); }}
                    data-testid="booking-new-button"
                    className="mt-6 block mx-auto text-sm font-semibold text-lake underline underline-offset-4 hover:text-pine"
                  >
                    Faire une autre demande
                  </button>
                </motion.div>
              )}

              {tab === "contact" && (
                <motion.form
                  key="contact"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.35 }}
                  onSubmit={submitContact}
                  className="mt-8 grid sm:grid-cols-2 gap-5"
                  data-testid="contact-form"
                >
                  <div>
                    <label className={labelCls}>Nom</label>
                    <input type="text" required placeholder="Votre nom" value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      className={inputCls} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" required placeholder="vous@exemple.fr" value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      className={inputCls} data-testid="contact-email-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Sujet</label>
                    <input type="text" required placeholder="Tarifs, animations, groupes…" value={contact.subject}
                      onChange={(e) => setContact({ ...contact, subject: e.target.value })}
                      className={inputCls} data-testid="contact-subject-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Message</label>
                    <textarea rows="4" required placeholder="Votre message…" value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      className={`${inputCls} resize-none`} data-testid="contact-message-input" />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="sm:col-span-2 rounded-full bg-lake text-sand font-semibold px-8 py-4 transition-all duration-300 hover:bg-pine disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 size={17} className="animate-spin" />}
                    Envoyer le message
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
